import { ChangeDetectionStrategy, Component, computed, inject, output, OutputEmitterRef, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { SendApplicationDirective } from '@games';
import {
  ActionModalComponent,
  AnalyticsService,
  AppRoutes,
  DashboardAnalyticsEvent,
  GameDetailsModel,
  GamesRoutes,
  LocalRefreshService,
  MODAL_TOKEN,
  ModalProvider,
  PreventMultiClickDirective
} from '@core';
import { RedirectTypeEnum } from '@ui-components';
import { Observable, tap } from 'rxjs';
import { IonicModule } from '@ionic/angular';
import { DeclineInvitationCommandHandler } from '@invitations';
import { SendApplicationCommandHandler } from '../../../../../games/src/lib/application/handlers';
import { Router } from '@angular/router';
import { GameApplicationsService } from '@games/infra';
import { InvitationsService } from '@invitations/infra';
import { GetMyGamesQueryHandler, LoadMyGamesCommandHandler } from '@my-games';

@Component({
  selector: 'lib-dashboard-invitations',
  templateUrl: './dashboard-invitations.component.html',
  styleUrls: ['./dashboard-invitations.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [IonicModule, SendApplicationDirective, PreventMultiClickDirective],
  providers: [
    GetMyGamesQueryHandler,
    SendApplicationCommandHandler,
    GameApplicationsService,
    LocalRefreshService,
    DeclineInvitationCommandHandler,
    InvitationsService
  ]
})
export class DashboardInvitationsComponent {
  private readonly getMyGamesQueryHandler: GetMyGamesQueryHandler = inject(GetMyGamesQueryHandler);
  private readonly loadMyGamesCommandHandler: LoadMyGamesCommandHandler = inject(LoadMyGamesCommandHandler);
  private readonly declineInvitationCommandHandler: DeclineInvitationCommandHandler = inject(
    DeclineInvitationCommandHandler
  );
  private readonly router: Router = inject(Router);
  private readonly modalProvider: ModalProvider = inject(MODAL_TOKEN);
  private readonly analyticsService: AnalyticsService = inject(AnalyticsService);

  readonly isInvitationsAvailable: OutputEmitterRef<boolean> = output<boolean>();
  readonly invitationsCount: OutputEmitterRef<number> = output<number>();
  readonly invitationAccepted: OutputEmitterRef<void> = output<void>();

  private readonly invitations$: Observable<GameDetailsModel[]> = this.getMyGamesQueryHandler.getInvitations$().pipe(
    tap((result: GameDetailsModel[]) => {
      this.isInvitationsAvailable.emit(!!result?.length);
      this.invitationsCount.emit(result?.length ?? 0);
    })
  );

  readonly gameRedirectType: RedirectTypeEnum = RedirectTypeEnum.GAME;
  readonly skeletonItems: readonly number[] = [0, 1];
  readonly invitations: Signal<GameDetailsModel[] | undefined> = toSignal<GameDetailsModel[]>(this.invitations$);

  private readonly isLoaded: Signal<boolean> = toSignal(this.getMyGamesQueryHandler.isLoaded$(), {
    initialValue: false
  });
  readonly isLoading: Signal<boolean> = computed<boolean>(() => !this.isLoaded());

  goToDetails(gameId: string): void {
    this.router.navigate([`${AppRoutes.GAMES}/${GamesRoutes.DETAILS}/${gameId}`]);
  }

  refresh(): void {
    this.loadMyGamesCommandHandler.load().subscribe();
    this.invitationAccepted.emit();
  }

  stopEventPropagation(event: MouseEvent): void {
    event.stopImmediatePropagation();
  }

  trackAccept(gameId: string): void {
    this.analyticsService.buttonClick(DashboardAnalyticsEvent.InvitationAccept, { gameId });
  }

  decline(invitaionId: string, gameId: string): void {
    this.analyticsService.buttonClick(DashboardAnalyticsEvent.InvitationDecline, { gameId });

    this.modalProvider.showModal$({
      component: ActionModalComponent,
      cssClass: 'present-modal',
      componentProps: {
        header: 'Odrzuć zaproszenie',
        message: 'Czy na pewno chcesz zrezygnować z dołączenia do tej gierki?',
        btnOk: 'Tak, zrezygnuj',
        btnCancel: 'Anuluj',
        action: () => this.executeDecline(invitaionId)
      }
    });
  }

  private executeDecline(invitaionId: string): void {
    this.declineInvitationCommandHandler.decline(invitaionId).subscribe(() => this.refresh());
  }
}
